import { ArrowRight, CheckCircle2, Phone, HelpCircle, ChevronDown, MessageCircle, Mail, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Contact from '../components/Contact';

export default function FAQPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    const element = document.getElementById('faq-content');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqCategories = [
    {
      category: 'Allgemeine Fragen',
      icon: HelpCircle,
      questions: [
        {
          question: 'Wie viel kostet ein Umzug von der Schweiz nach Deutschland?',
          answer: (navigate: any) => `Die Kosten hängen vom <strong>Umzugsvolumen</strong>, der <strong>Entfernung</strong> und den gewünschten Services ab. Ein <strong>2-Zimmer-Umzug</strong> beginnt bei ca. <strong>CHF 3'900.–</strong>, ein <strong>3-Zimmer-Umzug</strong> bei ca. <strong>CHF 5'900.–</strong> und ein <strong>4-Zimmer-Umzug</strong> bei ca. <strong>CHF 7'900.–</strong>. Nach einer <strong>kostenlosen Besichtigung</strong> erhalten Sie eine transparente <strong>Fixpreis-Offerte</strong> ohne versteckte Kosten. <a href="/preise" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Alle Preise im Überblick</a>.`
        },
        {
          question: 'Wie lange dauert ein Umzug von der Schweiz nach Deutschland?',
          answer: (navigate: any) => `Die Dauer hängt von der Entfernung und dem Umfang ab. Bei den meisten Umzügen dauert der <strong>Transport 1-2 Tage</strong>. Die komplette Abwicklung vom <strong>Beladen in der Schweiz bis zum Aufbau in Deutschland</strong> erfolgt in der Regel innerhalb von <strong>1-2 Tagen</strong>. <a href="/express-umzuege" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Express-Umzüge</a> sind innerhalb von <strong>24-48 Stunden</strong> möglich.`
        },
        {
          question: 'Bieten Sie auch Express-Umzüge an?',
          answer: (navigate: any) => `Ja! Wir bieten einen <a href="/express-umzuege" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Express-Service</a> an und können Ihren Umzug innerhalb von <strong>24-48 Stunden</strong> realisieren. Dies ist ideal für dringende oder kurzfristige Umzüge. Kontaktieren Sie uns für die schnelle Planung Ihres <strong>Express-Umzugs</strong>.`
        },
        {
          question: 'Kann ich auch nur Teile meines Hausrats transportieren lassen?',
          answer: (navigate: any) => `Absolut! Wir bieten auch <a href="/teilumzug" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Teilumzüge</a> an, bei denen wir nur einzelne Möbelstücke oder Teile Ihres Hausrats transportieren. Dies ist eine <strong>kosteneffiziente Lösung</strong>, wenn Sie nicht Ihren kompletten Haushalt umziehen möchten. Die Kosten richten sich nach dem tatsächlichen Volumen.`
        },
      ],
    },
    {
      category: 'Zoll & Formalitäten',
      icon: CheckCircle2,
      questions: [
        {
          question: 'Übernehmen Sie die Zollabwicklung?',
          answer: (navigate: any) => `Ja, die <strong>komplette <a href="/zollabwicklung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Zollabwicklung</a></strong> ist bei uns im Service inbegriffen. Wir erstellen alle erforderlichen <strong>Zolldokumente</strong> (Übersiedlungsgut-Formular, Inventarliste), begleiten den Transport über die Grenze und kümmern uns um alle <strong>Zollformalitäten</strong> – ohne dass Sie vor Ort sein müssen.`
        },
        {
          question: 'Welche Dokumente benötige ich für den Umzug nach Deutschland?',
          answer: (navigate: any) => `Für den Umzug von der Schweiz nach Deutschland benötigen Sie ein <strong>Übersiedlungsgut-Formular</strong> und eine detaillierte <strong>Inventarliste</strong>. Diese Dokumente erstellen wir für Sie. Zusätzlich benötigen wir von Ihnen eine Kopie Ihres <strong>Personalausweises</strong> oder <strong>Reisepasses</strong> sowie eine <strong>Wohnsitzbestätigung</strong> in Deutschland (z.B. Mietvertrag). <a href="/zollabwicklung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zur Zollabwicklung</a>.`
        },
        {
          question: 'Muss ich beim Zoll dabei sein?',
          answer: (navigate: any) => `Nein, Sie müssen <strong>nicht persönlich beim Zoll</strong> anwesend sein. Wir übernehmen die komplette <a href="/zollabwicklung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Zollabwicklung</a> für Sie. Sie können bequem in der Schweiz oder bereits in Deutschland sein, während wir uns um alle Formalitäten am Grenzübergang kümmern.`
        },
        {
          question: 'Fallen beim Umzug nach Deutschland Zollgebühren an?',
          answer: (navigate: any) => `Bei einem <strong>dauerhaften Wohnsitzwechsel</strong> von der Schweiz nach Deutschland fallen in der Regel <strong>keine Zollgebühren</strong> an. Ihr persönliches Übersiedlungsgut ist zollfrei, solange die Voraussetzungen erfüllt sind (Wohnsitzwechsel, Gegenstände seit mind. 6 Monaten in Ihrem Besitz). Wir beraten Sie gerne zu den Details. <a href="/zollabwicklung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zur Zollabwicklung</a>.`
        },
      ],
    },
    {
      category: 'Leistungen & Service',
      icon: CheckCircle2,
      questions: [
        {
          question: 'Übernehmen Sie auch die Verpackung meines Hausrats?',
          answer: (navigate: any) => `Ja! Wir bieten einen <strong>professionellen Verpackungsservice</strong> an. Unser Team verpackt Ihren kompletten Hausrat sorgfältig und fachgerecht. Alternativ können Sie auch <a href="/verpackungsmaterial" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Verpackungsmaterial bei uns bestellen</a> und selbst verpacken. Die Möbel werden von uns grundsätzlich professionell demontiert und geschützt.`
        },
        {
          question: 'Bieten Sie auch Möbelmontage an?',
          answer: (navigate: any) => `Ja, die <strong>professionelle Möbelmontage</strong> ist Teil unseres Standard-Services. Wir bauen Ihre Möbel in der Schweiz ab und am Zielort in Deutschland wieder fachgerecht auf. Dies umfasst Schränke, Betten, Regale und andere zerlegbare Möbelstücke. <a href="/komplettumzug" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zum Komplettumzug</a>.`
        },
        {
          question: 'Kann ich meine Sachen zwischenlagern?',
          answer: (navigate: any) => `Ja! Wir verfügen über <strong>sichere Lagerhallen</strong> und können Ihr Umzugsgut bei Bedarf <a href="/zwischenlagerung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">zwischenlagern</a>. Dies ist besonders praktisch, wenn Ihre neue Wohnung in Deutschland noch nicht bezugsbereit ist oder Sie zeitlich flexibel sein möchten. Die Lagerung erfolgt versichert und unter optimalen Bedingungen.`
        },
        {
          question: 'Bieten Sie auch eine Endreinigung an?',
          answer: (navigate: any) => `Ja! Wir bieten eine <a href="/endreinigung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">professionelle Endreinigung</a> Ihrer alten Wohnung in der Schweiz an. So können Sie sicherstellen, dass Sie die Wohnung in einwandfreiem Zustand übergeben und Ihre Kaution zurückerhalten. Die Reinigung erfolgt nach den Standards der Hausverwaltungen.`
        },
      ],
    },
    {
      category: 'Versicherung & Sicherheit',
      icon: CheckCircle2,
      questions: [
        {
          question: 'Ist mein Umzugsgut während des Transports versichert?',
          answer: (navigate: any) => `Ja, alle unsere Umzüge sind <strong>vollständig versichert</strong>. Wir verfügen über eine umfassende <strong>Transportversicherung</strong>, die Ihre Möbel und Ihr Umzugsgut während des gesamten Transports von der Schweiz nach Deutschland schützt. Bei Bedarf kann auch eine <strong>Zusatzversicherung</strong> für besonders wertvolle Gegenstände abgeschlossen werden. <a href="/leistungen" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zu unseren Leistungen</a>.`
        },
        {
          question: 'Was passiert, wenn etwas beschädigt wird?',
          answer: (navigate: any) => `Im unwahrscheinlichen Fall eines Schadens greift unsere <strong>Transportversicherung</strong>. Schäden werden dokumentiert und über die Versicherung reguliert. Wir arbeiten sehr sorgfältig und verpacken alle Gegenstände professionell, um Schäden zu vermeiden. Unsere <strong>Schadensquote liegt unter 0,5%</strong>. <a href="/leistungen" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zu unseren Leistungen</a>.`
        },
        {
          question: 'Wie sicher ist mein Hausrat bei der Zwischenlagerung?',
          answer: 'Unsere <strong>Lagerhallen sind vollständig gesichert</strong> mit Alarmanlagen, Videoüberwachung und Zugangskontrolle. Ihr Hausrat wird in <strong>abgeschlossenen Räumen</strong> gelagert und ist während der gesamten Lagerzeit versichert. Die Lagerhallen sind klimatisiert und trocken.'
        },
      ],
    },
    {
      category: 'Kosten & Zahlung',
      icon: CheckCircle2,
      questions: [
        {
          question: 'Wie kommt der Preis für meinen Umzug zustande?',
          answer: (navigate: any) => `Der Preis wird anhand mehrerer Faktoren berechnet: <strong>Umzugsvolumen</strong> (Anzahl und Größe Ihrer Möbel), <strong>Entfernung</strong> (von Ihrem Standort in der Schweiz zum Zielort in Deutschland), <strong>Stockwerk</strong> (mit/ohne Aufzug), <strong>Zusatzleistungen</strong> (Verpackung, Montage, etc.). Nach der kostenlosen Besichtigung erhalten Sie einen transparenten Festpreis. <a href="/preise" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Alle Preise im Überblick</a>.`
        },
        {
          question: 'Gibt es versteckte Kosten?',
          answer: 'Nein! Wir bieten eine <strong>Fixpreis-Garantie ohne versteckte Kosten</strong>. Der Preis, den Sie in unserer Offerte erhalten, ist der Endpreis. Es kommen keine zusätzlichen Gebühren für die Zollabwicklung, Treppenhäuser oder ähnliches hinzu – vorausgesetzt, die bei der Besichtigung festgestellten Gegebenheiten entsprechen der Realität.'
        },
        {
          question: 'Muss ich eine Anzahlung leisten?',
          answer: 'Nein, bei uns ist <strong>keine Anzahlung erforderlich</strong>. Sie bezahlen erst nach erfolgreichem Abschluss des Umzugs. Die Rechnung erhalten Sie nach der Übergabe am Zielort. Wir akzeptieren verschiedene Zahlungsmethoden: Banküberweisung, EC-Karte oder Barzahlung.'
        },
        {
          question: 'Kann ich die Kosten steuerlich absetzen?',
          answer: (navigate: any) => `In vielen Fällen ja! <strong>Beruflich bedingte Umzüge</strong> können in Deutschland steuerlich geltend gemacht werden. Auch in der Schweiz sind Umzugskosten bei einem berufsbedingten Wohnsitzwechsel absetzbar. Wir stellen Ihnen eine detaillierte Rechnung aus, die Sie beim Finanzamt einreichen können. Bitte konsultieren Sie Ihren Steuerberater für Details. <a href="/firmenumzuege" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zu Firmenumzügen</a>.`
        },
      ],
    },
    {
      category: 'Planung & Termine',
      icon: CheckCircle2,
      questions: [
        {
          question: 'Wie weit im Voraus sollte ich meinen Umzug planen?',
          answer: (navigate: any) => `Idealerweise <strong>2-4 Wochen im Voraus</strong>, besonders während der Hauptsaison (Mai-September). Bei dringenden Umzügen können wir auch kurzfristiger reagieren. Für <a href="/express-umzuege" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Express-Umzüge</a> innerhalb von 24-48 Stunden kontaktieren Sie uns bitte telefonisch. <a href="/ablauf" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Mehr zum Ablauf</a>.`
        },
        {
          question: 'Können Umzüge auch am Wochenende oder abends durchgeführt werden?',
          answer: (navigate: any) => `Ja! Wir bieten flexible Termine an, inklusive <strong>Wochenenden und Abendstunden</strong>. Dies ist besonders bei <a href="/firmenumzuege" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Firmenumzügen</a> praktisch, um den Geschäftsbetrieb nicht zu stören. Für Wochenend- und Abendtermine fällt ein geringer Aufschlag an.`
        },
        {
          question: 'Was passiert bei schlechtem Wetter?',
          answer: 'Schlechtes Wetter ist für uns kein Problem. Wir führen Umzüge bei <strong>jedem Wetter</strong> durch. Ihr Umzugsgut wird mit <strong>Schutzplanen und Decken</strong> vor Nässe geschützt. Bei extremen Witterungsverhältnissen (z.B. Schneefall, Glatteis) besprechen wir mit Ihnen eine mögliche Verschiebung.'
        },
        {
          question: 'Wie läuft die kostenlose Besichtigung ab?',
          answer: (navigate: any) => `Sie vereinbaren einen Termin mit uns. Ein <strong>Umzugsexperte</strong> kommt zu Ihnen nach Hause (oder führt eine <a href="/sofort-besichtigung" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Video-Besichtigung</a> durch) und erfasst Ihr Umzugsgut. Er bespricht mit Ihnen alle Details und klärt besondere Anforderungen. Innerhalb von <strong>24 Stunden</strong> erhalten Sie dann Ihre Fixpreis-Offerte per E-Mail. <a href="/offerte-anfordern" class="text-slate-800 underline hover:text-slate-600 font-semibold cursor-pointer">Jetzt Termin vereinbaren</a>.`
        },
      ],
    },
  ];

  const quickContacts = [
    {
      icon: Phone,
      title: 'Telefonisch',
      value: '0800 700 100',
      description: 'Gratis Hotline',
      link: 'tel:0800700100',
    },
    {
      icon: Mail,
      title: 'Per E-Mail',
      value: 'info@umzugprofis.ch',
      description: 'Antwort innerhalb 24h',
      link: 'mailto:info@umzugprofis.ch',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '+41 62 724 80 05',
      description: 'Schnelle Antwort',
      link: 'https://wa.me/41627248005',
    },
  ];

  return (
    <div className="min-h-screen">
      <section className="relative pt-24 md:pt-32 pb-24 min-h-[700px] bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-slate-500/5 animate-gradient"></div>
          <img
            src="/umzugsunternehmen-schweiz-deutschland.jpeg"
            alt="FAQ Umzug Schweiz Deutschland"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-green-50 text-green-700 px-4 py-2 rounded-full mb-6 border border-green-200 animate-fade-in hover:scale-105 transition-transform duration-300">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-semibold">Alle Antworten auf Ihre Fragen</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight animate-fade-in-delay">
              Häufig gestellte Fragen
              <span className="block text-slate-700 text-4xl md:text-5xl mt-2">
                Umzug Schweiz → Deutschland
              </span>
            </h1>

            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-delay-2">
              Hier finden Sie Antworten auf die wichtigsten Fragen rund um Ihren Umzug von der Schweiz nach Deutschland. Bei weiteren Fragen kontaktieren Sie uns gerne direkt.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12 animate-fade-in-delay-2">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2 text-lg"
              >
                <span>Persönliche Beratung anfordern</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-white hover:bg-slate-50 text-slate-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105 border-2 border-slate-800"
              >
                0800 700 100 (Gratis)
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto mb-20">
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">30+</div>
                <div className="text-slate-600 text-sm">Häufige Fragen beantwortet</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">6</div>
                <div className="text-slate-600 text-sm">Themenbereiche</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 animate-fade-in-delay-2">
                <div className="text-3xl font-bold text-slate-800 mb-1">24h</div>
                <div className="text-slate-600 text-sm">Antwortzeit per E-Mail</div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={scrollDown}
            className="animate-bounce bg-white hover:bg-slate-50 text-slate-800 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-slate-200"
            aria-label="Scroll down"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      <section id="faq-content" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {faqCategories.map((category, categoryIndex) => {
              const CategoryIcon = category.icon;
              return (
                <div key={categoryIndex} className="mb-16">
                  <div className="flex items-center space-x-3 mb-8">
                    <div className="bg-slate-800 w-12 h-12 rounded-xl flex items-center justify-center">
                      <CategoryIcon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                      {category.category}
                    </h2>
                  </div>

                  <div className="space-y-4">
                    {category.questions.map((faq, index) => {
                      const globalIndex = categoryIndex * 100 + index;
                      return (
                        <div
                          key={index}
                          className="bg-slate-50 rounded-xl border-2 border-slate-100 overflow-hidden transition-all duration-300 hover:border-slate-300 hover:shadow-lg"
                        >
                          <button
                            onClick={() => toggleFaq(globalIndex)}
                            className="w-full text-left p-6 flex items-center justify-between hover:bg-slate-100 transition-colors"
                          >
                            <h3 className="text-lg font-bold text-slate-900 pr-4">
                              {faq.question}
                            </h3>
                            <ChevronDown
                              className={`w-6 h-6 text-slate-600 flex-shrink-0 transition-transform duration-300 ${
                                openFaq === globalIndex ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          <div
                            className={`transition-all duration-300 ease-in-out ${
                              openFaq === globalIndex ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                            } overflow-hidden`}
                          >
                            <div
                              className="px-6 pb-6 text-slate-700 leading-relaxed [&_a]:transition-colors"
                              dangerouslySetInnerHTML={{ __html: typeof faq.answer === 'function' ? faq.answer(navigate) : faq.answer }}
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (target.tagName === 'A') {
                                  e.preventDefault();
                                  const href = target.getAttribute('href');
                                  if (href) navigate(href);
                                }
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Haben Sie weitere Fragen?
            </h2>
            <p className="text-xl text-slate-600">
              Kontaktieren Sie uns über einen der folgenden Kanäle
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
            {quickContacts.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <a
                  key={index}
                  href={contact.link}
                  className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 group text-center"
                >
                  <div className="bg-slate-800 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {contact.title}
                  </h3>
                  <div className="text-lg font-bold text-slate-800 mb-2">
                    {contact.value}
                  </div>
                  <p className="text-sm text-slate-600">
                    {contact.description}
                  </p>
                </a>
              );
            })}
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-10 text-center text-white">
              <Clock className="w-16 h-16 mx-auto mb-6 text-green-400" />
              <h3 className="text-3xl font-bold mb-4">
                Schnelle und kompetente Beratung
              </h3>
              <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Unser erfahrenes Team beantwortet gerne alle Ihre Fragen persönlich. Rufen Sie uns an oder schreiben Sie uns – wir sind für Sie da!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={scrollToContact}
                  className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 inline-flex items-center space-x-2"
                >
                  <span>Jetzt Kontakt aufnehmen</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a
                  href="tel:0800700100"
                  className="flex items-center space-x-2 text-white hover:text-slate-200 font-semibold transition"
                >
                  <Phone className="w-5 h-5" />
                  <span>0800 700 100 (Gratis)</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Bereit für Ihren Umzug?
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Lassen Sie sich jetzt kostenlos und unverbindlich beraten
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={scrollToContact}
                className="group bg-slate-800 hover:bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 flex items-center space-x-2"
              >
                <span>Kostenloses Angebot anfordern</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="tel:0800700100"
                className="flex items-center space-x-2 text-slate-800 hover:text-slate-900 font-semibold transition"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100 (Gratis)</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
